import { getRouteKeyFromPath, stripLanguageFromPath } from "@/lib/i18nRoutes";

// ── Route-key → canonical path mapping ───────────────────────────────────────
const routeKeyToPath = {
  home:           "/",
  navigation:     "/navigation",
  weather:        "/weather",
  safety:         "/safety",
  marinas:        "/marinas",
  radar:          "/radar",
  learningCenter: "/learning-center",
  trainingCenter: "/training-center",
  skipperTools:   "/skipper-tools",
  advertise:      "/advertise",
  feedback:       "/feedback",
  premium:        "/premium",
  custom:         "/custom",
  contact:        "/contact",
  privacy:        "/privacy",
  terms:          "/terms",
  cookies:        "/cookies",
  help:           "/help",
  affiliate:      "/affiliate",
  portEntry:      "/port-entry",
  routes:         "/routes",
  regulations:    "/regulations",
  glossary:       "/glossary",
};

// ── Default pages approved for Google indexing ────────────────────────────────
const ALWAYS_VISIBLE_PATHS = new Set(["/navigation"]);

const DEFAULT_INDEXED_PAGES = [
  "/",
  "/navigation",
  "/weather",
  "/weather",
  "/marinas",
  "/destinations",
  "/tools",
  "/learning-center",
  "/wire",
  "/premium",
  "/advertise",
  "/contact",
  "/privacy",
  "/terms",
  "/cookies",
  "/port-entry",
  "/routes",
  "/regulations",
  "/glossary",
  "/marinas-near-me",
  "/anchorages-near-me",
  "/fuel-near-me",
  "/marine-weather-near-me",
  "/boat-services-near-me",
];

// ── Paths served without a language prefix that should be indexed ─────────────
// These are NOT under /:lang/ but are still public, canonical pages.
const UNLOCALIZED_INDEXED_PATHS = new Set([
  "/contact",
  "/privacy",
  "/terms",
  "/cookies",
  "/port-entry",
  "/routes",
  "/regulations",
  "/glossary",
  "/wire",
  "/tools",
  "/marinas-near-me",
  "/anchorages-near-me",
  "/fuel-near-me",
  "/marine-weather-near-me",
  "/boat-services-near-me",
]);

// ── Regex for learning article paths: /learning/:category/:slug ───────────────
const LEARNING_ARTICLE_RE = /^\/learning\/[^/]+\/[^/?#]+/;
const PUBLIC_SEO_DETAIL_RE = /^\/(routes|marinas|regulations|glossary)\/[^/]+(?:\/[^/?#]+)?/;

export function getDefaultIndexedPages() {
  return DEFAULT_INDEXED_PAGES;
}

export function isPathApprovedForIndex(pathname, indexedPages = DEFAULT_INDEXED_PAGES) {
  const clean = stripLanguageFromPath(pathname).replace(/\/$/, "") || "/";

  // Language homepages like /en/, /ru/, /he/ must never be excluded only because
  // an old NavigationVisibilitySetting record forgot the bare home route.
  if (clean === "/") return true;

  // Learning articles and public SEO detail pages are indexable content pages
  if (LEARNING_ARTICLE_RE.test(clean) || PUBLIC_SEO_DETAIL_RE.test(clean)) return true;

  // Known unlocalized public pages are always indexable
  if (UNLOCALIZED_INDEXED_PATHS.has(clean)) return true;

  // Always merge default indexable routes with DB/editor settings. This prevents
  // stale Base44 entity values from accidentally emitting noindex on core pages.
  const allowed = new Set([...(indexedPages || []), ...DEFAULT_INDEXED_PAGES]);

  // For localized routes, check the normalized path against the allowed list
  const normalized = normalizeVisibilityPath(pathname);
  return allowed.has(normalized);
}

export function normalizeVisibilityPath(pathname) {
  const routeKey = getRouteKeyFromPath(pathname);
  if (routeKeyToPath[routeKey]) return routeKeyToPath[routeKey];
  return stripLanguageFromPath(pathname).replace(/\/$/, "") || "/";
}

export function isPathHidden(pathname, hiddenPages = []) {
  const normalized = normalizeVisibilityPath(pathname);
  if (ALWAYS_VISIBLE_PATHS.has(normalized)) return false;
  return hiddenPages.includes(normalized);
}